import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Paperclip } from "lucide-react";
import { useProducts, useCustomers, productOptions, customerOptions, nameOf, EXPENSE_CATEGORIES, BILLING_CYCLES } from "@/lib/lookups";

const PAYMENT_SOURCES = [
  { value: "__none__", label: "—" },
  { value: "business_bank", label: "NavScora Business Bank Account" },
  { value: "business_card", label: "NavScora Business Card" },
  { value: "director_personal", label: "Director — Personal Funds" },
  { value: "other", label: "Other" },
  { value: "unknown_historical", label: "Unknown / Historical" },
];
const PS_LABEL = Object.fromEntries(PAYMENT_SOURCES.map((o) => [o.value, o.label]));
const CURRENCIES = [{ value: "__none__", label: "—" }, ...["AUD", "USD", "NZD", "EUR", "GBP", "Other"].map((c) => ({ value: c, label: c }))];

const viewReceipt = async (id) => {
  try {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  } catch {
    toast.error("Could not open receipt");
  }
};

export default function Expenses() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });
  const { data: products } = useProducts();
  const { data: customers } = useCustomers();

  const save = useMutation({
    mutationFn: async (payload) => editing ? api.put(`/expenses/${editing.id}`, payload) : api.post("/expenses", payload),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Expense saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/expenses/${id}`), onSuccess: () => { qc.invalidateQueries(); toast.success("Expense voided"); } });

  const fields = [
    { name: "date", label: "Date", type: "date" },
    { name: "supplier", label: "Supplier", placeholder: "e.g. Cloudflare" },
    { name: "supplier_reference", label: "Supplier Invoice / Reference", placeholder: "e.g. Emergent INV-1234 (optional)" },
    { name: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES, default: "hosting" },
    { name: "cost_type", label: "COGS or OpEx", type: "select", options: [
      { value: "cogs", label: "Cost of Revenue (COGS)" }, { value: "opex", label: "Operating Expense" }], default: "cogs" },
    { name: "fixed_variable", label: "Fixed / Variable", type: "select", options: [
      { value: "fixed", label: "Fixed" }, { value: "variable", label: "Variable" }], default: "variable" },
    { name: "direct_indirect", label: "Direct / Indirect", type: "select", options: [
      { value: "direct", label: "Direct" }, { value: "indirect", label: "Indirect" }], default: "direct" },
    { name: "product_id", label: "Product", type: "select", options: productOptions(products) },
    { name: "customer_id", label: "Customer", type: "select", options: customerOptions(customers) },
    { name: "amount_ex", label: "Amount (ex-GST)", type: "number", step: "0.01" },
    { name: "gst_treatment", label: "GST Treatment", type: "select", options: [
      { value: "taxable", label: "Taxable (10%)" }, { value: "gst_free", label: "GST-free" }], default: "taxable" },
    { name: "recurring", label: "Recurring cost", type: "switch" },
    { name: "billing_cycle", label: "Billing Cycle", type: "select", options: BILLING_CYCLES, default: "monthly" },
    { name: "payment_status", label: "Payment Status", type: "select", options: [
      { value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }], default: "unpaid" },
    { name: "payment_source", label: "Payment Source", type: "select", options: PAYMENT_SOURCES },
    { name: "original_currency", label: "Original Currency", type: "select", options: CURRENCIES },
    { name: "original_amount", label: "Original Amount", type: "number", step: "0.01" },
    { name: "aud_amount_paid", label: "AUD Amount Paid", type: "number", step: "0.01" },
    { name: "document_id", label: "Receipt / Invoice", type: "document", full: true },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];

  const columns = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: "supplier", header: "Supplier" },
    { key: "category", header: "Category", render: (r) => <span className="capitalize">{(r.category || "").replace(/_/g, " ")}</span> },
    { key: "cost_type", header: "Class", render: (r) => <StatusBadge status={r.cost_type} /> },
    { key: "fixed_variable", header: "F/V", render: (r) => <StatusBadge status={r.fixed_variable} /> },
    { key: "product_id", header: "Product", render: (r) => nameOf(products, r.product_id) },
    { key: "amount_ex", header: "Ex-GST", align: "right", mono: true, render: (r) => fmtMoney(r.amount_ex) },
    { key: "gst", header: "GST", align: "right", mono: true, render: (r) => fmtMoney(r.gst) },
    { key: "amount_inc", header: "Inc-GST", align: "right", mono: true, render: (r) => fmtMoney(r.amount_inc) },
    { key: "payment_status", header: "Status", render: (r) => <StatusBadge status={r.payment_status} /> },
    { key: "payment_source", header: "Source", render: (r) => r.payment_source ? (
      r.payment_source === "director_personal"
        ? <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-300">Director funded</span>
        : <span className="text-xs text-slate-300">{PS_LABEL[r.payment_source] || r.payment_source}</span>
    ) : <span className="text-slate-600">—</span> },
    { key: "document_id", header: "Receipt", render: (r) => r.document_id
      ? <button className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:underline" onClick={(e) => { e.stopPropagation(); viewReceipt(r.document_id); }} data-testid={`view-receipt-${r.id}`}><Paperclip className="h-3 w-3" /> View</button>
      : <span className="text-slate-600">—</span> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }} data-testid={`edit-expense-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={(e) => { e.stopPropagation(); del.mutate(r.id); }} data-testid={`delete-expense-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Expenses" subtitle="Cost of revenue and operating expenses" testId="expenses-header">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-expense-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Expense
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={rows} testId="expense-ledger-table" empty={{ title: "No expenses recorded", message: "Add your first supplier cost or infrastructure expense." }} />
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Expense" : "Add Expense"} fields={fields} initial={editing} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="expense-form" />
    </div>
  );
}
